/**
 * DateSuggestionPopover Component
 * Popover with search input and date suggestion list.
 * Uses Radix Popover (shadcn/ui) for positioning and focus trapping.
 */

import { useState, useRef, useEffect } from 'react';
import { Calendar, Sun, Clock, CalendarX } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { getDateSuggestions } from './dateSuggestions';
import type { DateSuggestion, DateSuggestionIcon } from './dateSuggestions';
import { getCampaignToday } from '@/lib/calendarUtils';

// ============================================================================
// Types
// ============================================================================

export interface DateSuggestionPopoverProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (date?: string, time?: string) => void;
  children: React.ReactNode;
}

// ============================================================================
// Icon Mapping
// ============================================================================

const ICON_MAP: Record<DateSuggestionIcon, React.ElementType> = {
  today: Calendar,
  tomorrow: Sun,
  calendar: Calendar,
  clock: Clock,
  none: CalendarX,
};

// ============================================================================
// Component
// ============================================================================

export function DateSuggestionPopover({
  open,
  onOpenChange,
  onSelect,
  children,
}: DateSuggestionPopoverProps) {
  const [searchInput, setSearchInput] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const suggestions = getDateSuggestions(searchInput, getCampaignToday());

  // Reset state when popover opens
  useEffect(() => {
    if (open) {
      setSearchInput('');
      setHighlightedIndex(0);
      // Auto-focus the search input after popover animation
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  // Clamp highlighted index when suggestions shrink
  useEffect(() => {
    if (highlightedIndex >= suggestions.length) {
      setHighlightedIndex(Math.max(0, suggestions.length - 1));
    }
  }, [suggestions.length, highlightedIndex]);

  function handleSelect(suggestion: DateSuggestion): void {
    onSelect(suggestion.date, suggestion.time);
    onOpenChange(false);
  }

  function handleKeyDown(e: React.KeyboardEvent): void {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((i) => Math.min(i + 1, suggestions.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((i) => Math.max(i - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        e.stopPropagation();
        if (suggestions[highlightedIndex]) {
          handleSelect(suggestions[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        onOpenChange(false);
        break;
    }
  }

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align="end" sideOffset={4} className="w-64 p-0">
        <div onKeyDown={handleKeyDown}>
          {/* Search input */}
          <div className="p-2 border-b">
            <input
              ref={inputRef}
              type="text"
              placeholder="Type date and time"
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                setHighlightedIndex(0);
              }}
              className="w-full h-8 px-2 text-sm rounded-md border border-input bg-background
                placeholder:text-muted-foreground
                focus:outline-none focus:ring-1 focus:ring-ring"
            />
          </div>

          {/* Suggestion list */}
          <div className="py-1 max-h-[240px] overflow-y-auto">
            {suggestions.map((suggestion, index) => {
              const Icon = ICON_MAP[suggestion.icon];
              return (
                <button
                  key={`${suggestion.date ?? 'none'}-${suggestion.time ?? 'none'}-${index}`}
                  type="button"
                  onClick={() => handleSelect(suggestion)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  className={cn(
                    'flex items-center justify-between w-full px-3 py-2 text-sm cursor-pointer',
                    'hover:bg-accent transition-colors',
                    index === highlightedIndex && 'bg-accent'
                  )}
                >
                  <span className="flex items-center gap-2">
                    <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="font-medium">{suggestion.label}</span>
                  </span>
                  <span className="text-xs text-muted-foreground">{suggestion.detail}</span>
                </button>
              );
            })}
            {suggestions.length === 0 && (
              <div className="px-3 py-2 text-sm text-muted-foreground">No matches</div>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
